import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SafetyTestService } from '../../core/services/safety-test.service';
import { SafetyTestCategoryService } from '../../core/services/safety-test-category.service';
import {
  SafetyTestWithQuestions,
  SafetyTestCategory,
  TestVersionHistory,
  AdminTestStatus
} from '../../core/models';

declare var bootstrap: any;

@Component({
  selector: 'app-safety-test-detail',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './safety-test-detail.component.html',
  styleUrl: './safety-test-detail.component.scss'
})
export class SafetyTestDetailComponent implements OnInit {
  test: SafetyTestWithQuestions | null = null;
  category: SafetyTestCategory | null = null;
  versionHistory: TestVersionHistory[] = [];
  loading = true;
  errorMessage = '';
  testId: string | null = null;

  // Version history
  showVersionHistory = false;
  loadingVersions = false;

  // Modals
  private deleteModal: any;
  private activateModal: any;
  private newVersionModal: any;
  deleting = false;
  activating = false;
  creatingVersion = false;
  newVersionNotes = '';

  // Question expansion state
  expandedQuestions = new Set<string>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private safetyTestService: SafetyTestService,
    private categoryService: SafetyTestCategoryService
  ) {}

  ngOnInit(): void {
    this.testId = this.route.snapshot.paramMap.get('id');
    if (this.testId) {
      this.loadTestDetail();
    } else {
      this.errorMessage = 'No test ID provided';
      this.loading = false;
    }
  }

  async loadTestDetail(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      this.test = await this.safetyTestService.getTest(this.testId!);

      if (!this.test) {
        this.errorMessage = 'Test not found';
        return;
      }

      // Load category if exists
      if (this.test.categoryId) {
        this.category = await this.categoryService.getCategory(this.test.categoryId);
      }
    } catch (error: any) {
      console.error('Failed to load test:', error);
      this.errorMessage = 'Failed to load test details';
    } finally {
      this.loading = false;
    }
  }

  async loadVersionHistory(): Promise<void> {
    if (!this.testId || this.versionHistory.length > 0) return;

    this.loadingVersions = true;
    try {
      this.versionHistory = await this.safetyTestService.getVersionHistory(this.testId);
    } catch (error: any) {
      console.error('Failed to load version history:', error);
    } finally {
      this.loadingVersions = false;
    }
  }

  toggleVersionHistory(): void {
    this.showVersionHistory = !this.showVersionHistory;
    if (this.showVersionHistory && this.versionHistory.length === 0) {
      this.loadVersionHistory();
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/safety-tests']);
  }

  editTest(): void {
    this.router.navigate(['/safety-tests', this.testId, 'edit']);
  }

  // Question expansion
  toggleQuestion(questionId: string): void {
    if (this.expandedQuestions.has(questionId)) {
      this.expandedQuestions.delete(questionId);
    } else {
      this.expandedQuestions.add(questionId);
    }
  }

  isQuestionExpanded(questionId: string): boolean {
    return this.expandedQuestions.has(questionId);
  }

  expandAllQuestions(): void {
    if (this.test?.questions) {
      this.test.questions.forEach(q => this.expandedQuestions.add(q.id));
    }
  }

  collapseAllQuestions(): void {
    this.expandedQuestions.clear();
  }

  // Delete modal
  openDeleteModal(): void {
    const modalElement = document.getElementById('deleteModal');
    if (modalElement) {
      this.deleteModal = new bootstrap.Modal(modalElement);
      this.deleteModal.show();
    }
  }

  async deleteTest(): Promise<void> {
    if (!this.testId || this.deleting) return;

    this.deleting = true;

    try {
      const success = await this.safetyTestService.deleteTest(this.testId);

      if (success) {
        if (this.deleteModal) {
          this.deleteModal.hide();
        }
        this.router.navigate(['/safety-tests']);
      } else {
        this.errorMessage = 'Failed to delete test';
      }
    } catch (error: any) {
      console.error('Failed to delete test:', error);
      this.errorMessage = 'Failed to delete test';
    } finally {
      this.deleting = false;
    }
  }

  // Activate modal
  openActivateModal(): void {
    const modalElement = document.getElementById('activateModal');
    if (modalElement) {
      this.activateModal = new bootstrap.Modal(modalElement);
      this.activateModal.show();
    }
  }

  async activateTest(): Promise<void> {
    if (!this.testId || this.activating) return;

    this.activating = true;

    try {
      const updated = await this.safetyTestService.activateTest(this.testId);

      if (updated) {
        this.test = { ...this.test!, ...updated };
        if (this.activateModal) {
          this.activateModal.hide();
        }
      } else {
        this.errorMessage = 'Failed to activate test';
      }
    } catch (error: any) {
      console.error('Failed to activate test:', error);
      this.errorMessage = 'Failed to activate test';
    } finally {
      this.activating = false;
    }
  }

  // New version modal
  openNewVersionModal(): void {
    this.newVersionNotes = '';
    const modalElement = document.getElementById('newVersionModal');
    if (modalElement) {
      this.newVersionModal = new bootstrap.Modal(modalElement);
      this.newVersionModal.show();
    }
  }

  async createNewVersion(): Promise<void> {
    if (!this.testId || this.creatingVersion) return;

    this.creatingVersion = true;

    try {
      const newTest = await this.safetyTestService.createNewVersion(this.testId, {
        versionNotes: this.newVersionNotes || undefined
      });

      if (newTest) {
        if (this.newVersionModal) {
          this.newVersionModal.hide();
        }
        // Navigate to the new test
        this.router.navigate(['/safety-tests', newTest.id, 'edit']);
      } else {
        this.errorMessage = 'Failed to create new version';
      }
    } catch (error: any) {
      console.error('Failed to create new version:', error);
      this.errorMessage = 'Failed to create new version';
    } finally {
      this.creatingVersion = false;
    }
  }

  // Helpers
  getStatusClass(status: AdminTestStatus): string {
    switch (status) {
      case 'active': return 'bg-success';
      case 'draft': return 'bg-secondary';
      case 'retired': return 'bg-warning';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: AdminTestStatus): string {
    switch (status) {
      case 'active': return 'Active';
      case 'draft': return 'Draft';
      case 'retired': return 'Retired';
      default: return status;
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string | undefined): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getQuestionTypeBadge(type: string): string {
    return type === 'true_false' ? 'True/False' : 'Multiple Choice';
  }

  canActivate(): boolean {
    return this.test?.status === 'draft' &&
           (this.test?.questions?.length || 0) > 0;
  }

  canDelete(): boolean {
    return this.test?.status === 'draft';
  }

  canCreateNewVersion(): boolean {
    return this.test?.status === 'active' || this.test?.status === 'retired';
  }

  // CSV Export
  exportQuestions(): void {
    if (!this.test?.questions || this.test.questions.length === 0) return;

    const csvContent = this.buildQuestionsCsv(this.test.questions);
    const filename = `questions-${this.test.shortName || this.testId?.substring(0, 8)}-${new Date().toISOString().split('T')[0]}.csv`;
    this.downloadCsv(csvContent, filename);
  }

  async exportResults(): Promise<void> {
    if (!this.testId) return;

    try {
      const blob = await this.safetyTestService.exportResults(this.testId);
      if (blob) {
        this.downloadBlob(blob, `results-${this.test?.shortName || this.testId.substring(0, 8)}.csv`);
      }
    } catch (error: any) {
      console.error('Failed to export results:', error);
      this.errorMessage = 'Failed to export results';
    }
  }

  private buildQuestionsCsv(questions: any[]): string {
    const header = [
      'Question Number',
      'Question Text',
      'Question Type',
      'Explanation',
      'Option A', 'Option A Correct',
      'Option B', 'Option B Correct',
      'Option C', 'Option C Correct',
      'Option D', 'Option D Correct',
      'Option E', 'Option E Correct',
      'Option F', 'Option F Correct'
    ].join(',');

    const rows = questions.map((q, index) => {
      const row: string[] = [
        (index + 1).toString(),
        this.escapeCsvField(q.text),
        q.type,
        this.escapeCsvField(q.explanation || '')
      ];

      for (let i = 0; i < 6; i++) {
        if (q.options && q.options[i]) {
          row.push(this.escapeCsvField(q.options[i].text));
          row.push(q.options[i].isCorrect ? 'TRUE' : 'FALSE');
        } else {
          row.push('');
          row.push('');
        }
      }

      return row.join(',');
    });

    return [header, ...rows].join('\n');
  }

  private escapeCsvField(field: string): string {
    if (!field) return '';
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
      return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
  }

  private downloadCsv(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, filename);
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
